/* globals document */

import React, { StrictMode, Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider, useSelector } from 'react-redux';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useNavigation,
  useLocation,
  useParams,
} from 'react-router';
import { LoadingScreen } from './map/next/components/preload-screens';
import { loadGTM } from '@birdi/gtm';
import { loadFirstPromoter } from '@birdi/first-promoter';
import { loadHubspot } from '@birdi/hubspot';
import { Toaster, ToastBar } from 'react-hot-toast';
import { isUploadToastId } from './common/upload-toast';
import { config } from '@birdi/js-sdk';
import { css } from '@emotion/react';
import { ThemeProvider, breakpoints } from '@birdi/theme';
import { MetaBar } from '@birdi/dev-tools/MetaBar';
import {
  AppErrorBoundary,
  RouteErrorBoundary,
  reloadForChunkError,
} from '@birdi/crash-screen';
import { NotFoundScreen } from './meta/NotFoundScreen';
import { loadIntercom, showIntercom, hideIntercom } from '@birdi/intercom';
import { store } from './state';
// routes
import { getInitialAuthState } from './authentication/actions';
import { RequireAuth } from './authentication/AuthenticatedRoute';
// old - authentication
// import AuthContainer from './authentication';
// import SignIn from './authentication/SignInPage';
// import SignUp from './authentication/SignUpPage';
// import ResetPassword from './authentication/ResetPassword';
// import ResetOrganisationPassword from './authentication/ResetOrganisationPassword';
// import ResendVerification from './authentication/ResendVerification';
// import VerifyEmailWithToken from './authentication/VerifyEmailWithToken';
// old - onboarding
// import Onboarding1Welcome from './onboarding/Step1Welcome';
// import Onboarding2Workspace from './onboarding/Step2Workspace';
// import Onboarding3ThankYou from './onboarding/Step3ThankYou';
// new authentication
import OnboardingContainer from './authentication/new-design';
import SignUpPage from './authentication/new-design/sign-up-page';
import SignInPage from './authentication/new-design/sign-in-page';
import ResetPasswordPage from './authentication/new-design/reset-password';
import ResetOrganisationPasswordPage from './authentication/new-design/reset-organisation-password';
import ResendVerificationPage from './authentication/new-design/resend-verification';
import VerifyYourEmailPage from './authentication/new-design/verify-your-email';
import VerifyEmailWithTokenPage from './authentication/new-design/verify-email-with-token';
import InvitedUserFormPage from './authentication/new-design/invited-user-form-page';
import SSOCallbackPage from './authentication/new-design/sso-callback-page';
// new - onboarding
import OrganisationSetupPage from './authentication/new-design/onboarding/organisation-setup';
import SelectPlanPage from './authentication/new-design/onboarding/select-plan';
import InviteYourTeamPage from './authentication/new-design/onboarding/invite-your-team';
import SelectDataRegionPage from './authentication/new-design/onboarding/select-data-region';
// dashboard & maps
// import { Dashboard } from './dashboard/main';
import { Dashboard } from './dashboard-new';
import { AssistantWidget } from './assistant/AssistantWidget';
import { useSnapshot } from 'valtio';
import { assistantUi, isAssistantHiddenPath } from './assistant/map-bridge';
import { useAssistantSelector } from './assistant/reducer';
import { AllMissions } from './missions/all-missions';
import { AllNewMaps } from './missions/all-maps';
import { IncomingJobRequests } from './incoming-request/index';
import { SharedMissions } from './missions/SharedMissions';
import { UnauthenticatedShare } from './unauthenticated-share/UnauthenticatedShare';
import { AllSites } from './map-sites';
import { SitePage } from './map-sites/SitePage';
// administration
import { General } from './administration/general';
import { Users } from './administration/users';
import { Workspaces } from './administration/workspaces';
import { Invoices } from './administration/billing';
import { PaymentMethod } from './administration/billing/payment-method';
import { PlanAndUsage } from './administration/plan-and-usage';
import { InvoicesV2 } from './administration/billing/invoices-v2';
import { PlanAndUsageV2 } from './administration/plan-and-usage/index-v2';
import { PlanOptions } from './administration/plan-and-usage/change-plan/plan-options';
import ProcessingView from './administration/processing';
import { Integrations } from './administration/integrations';
// default in avatar menu
import { CostCalculator } from './administration/processing/cost-calculator';
// profile
import { General as ProfileGeneral } from './profile/general';
import ProfilePermissions from './profile/permissions';
import ProfileWorkspaces from './profile/workspaces';
// workspaces
import WorkspacesGeneral from './workspaces/general';
import WorkspacesCustomization from './workspaces/customization';
import WorkspacesUsers from './workspaces/users';
import WorkspacesProcessing from './workspaces/processing';
import WorkspacesPermissions from './workspaces/permissions';
import WorkspacesDataTags from './workspaces/data-tags';
// inspection tools
import { AssessmentType } from './inspection-tools/assessment-type';
import { AssessmentSeverity } from './inspection-tools/assessment-severity';
import { InspectionSummary } from './inspection-tools/inspection-summary';
import Assets from './inspection-tools/assets';
// pilot settings
import { PilotProfile } from './pilot-settings/pilot-profile';
import { Credentials } from './pilot-settings/credentials';
import { EquipmentSingle } from './pilot-settings/equipment-single';
import { Equipment } from './pilot-settings/equipment';
// layouts
import { SidebarLayout } from './layouts/sidebar-layout';
import { DefaultLayout } from './layouts/default-layout';
import { AccountLocked } from './authentication/Account-locked';
import { MapLayout } from './map/map-layout';
import { RootState } from '@birdi/types/redux-state';

// Set API routes from Webpack injected defaults
config.setOpts({
  API_URL: process.env.API_URL,
  VECTOR_SERVICE_URL: process.env.VECTOR_SERVICE_URL,
  JWT_NAME: process.env.JWT_NAME,
  S3_ZIP_URL: process.env.S3_ZIP_URL,
  POINTS_URL: process.env.POINTS_URL,
});

if (process.env.COOKIE_DOMAIN) {
  config.setOpts({
    COOKIE_DOMAIN: process.env.COOKIE_DOMAIN,
  });
}

const DevTools = React.lazy(() =>
  import('@birdi/dev-tools/DevTools').then((m) => ({ default: m.DevTools })),
);

// The dashboard home route — the one page the Intercom launcher is shown on.
const DASHBOARD_PATH = '/';

if (process.env.INTERCOM_ENABLED) {
  // The launcher bubble shows ONLY on the dashboard. Everywhere else Scout is
  // the support entry point — its contact_support handoff opens the messenger
  // directly (openSupportChat), which works with the launcher hidden — so a
  // standing bubble beside the Ask Birdi's AI Agent tab was noise. RootLayout's
  // effect below flips it on route change; boot hidden unless the first page IS
  // the dashboard, so a direct dashboard load doesn't flash the bubble away and
  // back. Intercom outbound messages/tours still deliver regardless.
  loadIntercom(window.location.pathname !== DASHBOARD_PATH);
}

if (process.env.GTM_ENABLED) {
  loadGTM();
}

if (process.env.FIRST_PROMOTER_ENABLED) {
  loadFirstPromoter({
    domain: process.env.COOKIE_DOMAIN,
    testMode: process.env.FIRST_PROMOTER_TEST_MODE,
    proxy: `${process.env.API_URL}/v2/service/fprom_proxy`,
  });
}

if (process.env.HUBSPOT_TRACKING_ENABLED) {
  loadHubspot();
}

if (process.env.POSTHOG_ENABLED && process.env.POSTHOG_API_KEY) {
  posthog.init(process.env.POSTHOG_API_KEY, {
    api_host: process.env.POSTHOG_API_HOST,
  });
}

const RedirectToMap = () => {
  const { id } = useParams();
  // Redirect from our OLD missions page link to our new one.
  // TODO: consider tracking usage and seeing if it's possible to remove.
  return <Navigate to={`/map/missions/${id}`} replace />;
};

const lazyMission = () =>
  import('./map/map/App').then((m) => ({ Component: m.default }));

const RootLayout = () => {
  const navigation = useNavigation();
  const location = useLocation();
  // Reserve a right-hand column for the assistant when its side panel is open,
  // so it sits beside the app (map/dashboard/nav) instead of overlaying it.
  const { isOpen: assistantOpen, isMinimized: assistantMinimized } =
    useAssistantSelector();
  // ...unless the route hides the assistant altogether (the Sites pages,
  // BIR-321) — the panel isn't rendered there, so reserving its column would
  // leave a blank strip down the right-hand side.
  const sidePanelOpen =
    assistantOpen &&
    !assistantMinimized &&
    !isAssistantHiddenPath(location.pathname);
  // The panel is user-resizable, so the reserved column has to track its LIVE
  // width — a fixed constant here would overlap or leave a gap after a drag.
  const assistantWidth = useSnapshot(assistantUi).width;

  const isNavigatingToMap =
    navigation.state === 'loading' &&
    (navigation.location?.pathname?.startsWith('/map') ?? false);

  // Show the Intercom launcher on the dashboard only; hide it everywhere else.
  // Scout can still summon the messenger from any page (openSupportChat calls
  // Intercom('show'), which opens it with the launcher hidden). During a
  // pending navigation we react to the DESTINATION so the bubble appears as the
  // dashboard starts loading and disappears as you leave it, rather than
  // lagging a frame behind the route.
  useEffect(() => {
    if (!process.env.INTERCOM_ENABLED) return;
    const pendingTo =
      navigation.state === 'loading'
        ? navigation.location?.pathname
        : undefined;
    const effectivePath = pendingTo ?? location.pathname;
    if (effectivePath === DASHBOARD_PATH) {
      showIntercom();
    } else {
      hideIntercom();
    }
  }, [location.pathname, navigation.state, navigation.location?.pathname]);

  // Map routes render their own <Toaster> (the new map centres it; the old map
  // puts it top-right), so the global one must NOT mount there — otherwise every
  // toast renders twice, once per Toaster. Off the map, this is the only Toaster.
  const isMapRoute =
    location.pathname.startsWith('/map') ||
    (navigation.state === 'loading' &&
      (navigation.location?.pathname?.startsWith('/map') ?? false));

  // The Sites pages must not surface the global upload-progress toast raised by
  // the map uploader (it leaks here because react-hot-toast's store is global).
  // Hide only those toasts, only on Sites — every other screen is unchanged.
  const isSitesRoute =
    location.pathname === '/sites' || location.pathname.startsWith('/sites/');

  return (
    <>
      {isNavigatingToMap && <LoadingScreen />}
      <div
        css={css`
          transition: padding-right 0.2s ease;
          @media (min-width: ${breakpoints.s}) {
            padding-right: ${sidePanelOpen ? assistantWidth : 0}px;
          }
        `}
      >
        <Outlet />
      </div>
      {!isMapRoute && (
        <Toaster position="top-right" containerStyle={{ top: 80 }}>
          {(t) =>
            isSitesRoute && isUploadToastId(t.id) ? (
              <></>
            ) : (
              <ToastBar toast={t} />
            )
          }
        </Toaster>
      )}
      <AssistantWidget />
    </>
  );
};

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    ErrorBoundary: RouteErrorBoundary,
    children: [
      // new authentication
      {
        path: '/sign-in',
        element: (
          <OnboardingContainer
            Section={SignInPage}
            leftSideImage="timeline-volume"
          />
        ),
      },
      {
        path: '/sign-up',
        element: (
          <OnboardingContainer
            Section={SignUpPage}
            leftSideImage="timeline-volume"
          />
        ),
      },
      {
        path: '/reset-password',
        element: (
          <OnboardingContainer
            Section={ResetPasswordPage}
            leftSideImage="timeline-volume"
          />
        ),
      },
      {
        path: '/email-verification',
        element: (
          <OnboardingContainer
            Section={VerifyEmailWithTokenPage}
            leftSideImage="vector-layers"
          />
        ),
      },
      {
        path: '/set-password',
        element: (
          <OnboardingContainer
            Section={ResetOrganisationPasswordPage}
            leftSideImage="timeline-volume"
          />
        ),
      },
      {
        path: '/resend-verification',
        element: (
          <OnboardingContainer
            Section={ResendVerificationPage}
            leftSideImage="vector-layers"
          />
        ),
      },
      {
        path: '/invited-user-signup',
        element: (
          <OnboardingContainer
            Section={InvitedUserFormPage}
            leftSideImage="timeline-volume"
          />
        ),
      },
      {
        path: '/sso/callback',
        ErrorBoundary: RouteErrorBoundary,
        element: (
          <OnboardingContainer
            Section={SSOCallbackPage}
            leftSideImage="timeline-volume"
          />
        ),
      },
      {
        path: '/onboarding',
        element: (
          <RequireAuth>
            <OnboardingContainer
              Section={OrganisationSetupPage}
              leftSideImage="dashboard"
            />
          </RequireAuth>
        ),
      },
      {
        path: '/onboarding/select-data-region',
        element: (
          <RequireAuth>
            <OnboardingContainer
              Section={SelectDataRegionPage}
              leftSideImage="vector-layers"
            />
          </RequireAuth>
        ),
      },
      {
        path: '/onboarding/select-plan',
        element: (
          <RequireAuth>
            <OnboardingContainer
              Section={SelectPlanPage}
              leftSideImage="map-export"
            />
          </RequireAuth>
        ),
      },
      {
        path: '/onboarding/invite-your-team',
        element: (
          <RequireAuth>
            <OnboardingContainer
              Section={InviteYourTeamPage}
              leftSideImage="comment"
            />
          </RequireAuth>
        ),
      },
      // dashboard
      {
        path: '/',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="dashboard">
              <Dashboard />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/sites',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="dashboard">
              <AllSites />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/sites/:id',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="dashboard">
              <SitePage />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      // map
      {
        path: '/missions/:id/map',
        element: <RedirectToMap />,
      },
      // dashboard - filtered missions
      {
        path: '/missions/mine',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="dashboard">
              <AllMissions association="mine" />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/missions',
        element: <Navigate to="/missions/mine" replace />,
      },
      {
        path: '/missions/organisation',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="dashboard">
              <AllMissions association="organisation" />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/missions/demo-missions',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="dashboard">
              <AllMissions association="sample" />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/missions/shared',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="dashboard">
              <SharedMissions />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/new-maps/all',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="dashboard">
              <AllNewMaps />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/new-maps/folder/:folderId',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="dashboard">
              <AllNewMaps />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      // mission share invitation
      {
        path: '/share/:id',
        element: <UnauthenticatedShare />,
      },
      // dashboard - Job requests
      {
        path: '/job-requests/incoming',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="dashboard">
              <IncomingJobRequests />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      // administration
      {
        path: '/admin-general',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="admin">
              <General />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/admin-users',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="admin">
              <Users />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/admin-workspaces',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="admin">
              <Workspaces />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/admin-billing',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="admin">
              <InvoicesV2 />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/admin-billing/payment-method',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="admin">
              <PaymentMethod />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/admin-plan',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="admin">
              <PlanAndUsageV2 />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/billing/options',
        element: (
          <RequireAuth>
            <DefaultLayout>
              <PlanOptions />
            </DefaultLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/admin-processing',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="admin">
              <ProcessingView />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/admin-integrations',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="admin">
              <Integrations />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      // workspaces
      {
        path: '/workspace-general/:orgId',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="workspace">
              <WorkspacesGeneral />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/workspace-customization/:orgId',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="workspace">
              <WorkspacesCustomization />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/workspace-users/:orgId',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="workspace">
              <WorkspacesUsers />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/workspace-processing/:orgId',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="workspace">
              <WorkspacesProcessing />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/workspace-permissions/:orgId',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="workspace">
              <WorkspacesPermissions />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/workspace-data-tags/:orgId',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="workspace">
              <WorkspacesDataTags />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      // profile
      {
        path: '/profile-general/:scopeId',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="user-profile">
              <ProfileGeneral />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/profile-permissions/:scopeId',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="user-profile">
              <ProfilePermissions />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/profile-workspaces/:scopeId',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="user-profile">
              <ProfileWorkspaces />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      // pilot network
      {
        path: '/user/pilot',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="pilot-settings">
              <PilotProfile />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/user/credentials',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="pilot-settings">
              <Credentials />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/equipment',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="pilot-settings">
              <Equipment />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/equipment/:id',
        element: (
          <RequireAuth>
            <EquipmentSingle />
          </RequireAuth>
        ),
      },
      // inspection tools
      {
        path: '/assessment-types',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="inspection-tools">
              <AssessmentType />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/assessment-severity',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="inspection-tools">
              <AssessmentSeverity />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/inspection-summary',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="inspection-tools">
              <InspectionSummary />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/assets',
        element: (
          <RequireAuth>
            <SidebarLayout sidebar="dashboard">
              <Assets />
            </SidebarLayout>
          </RequireAuth>
        ),
      },
      {
        path: '/authentication/account-locked',
        element: (
          <RequireAuth>
            <AccountLocked />
          </RequireAuth>
        ),
      },
      // dev
      {
        path: '/dev/:resource',
        element: <DevTools />,
      },
      // Maps and missions
      {
        path: 'map',
        Component: MapLayout,
        children: [
          // v2 Maps
          {
            path: 'next/:id',
            lazy: () =>
              import('./map/next/app').then((m) => ({ Component: m.default })),
          },
          {
            path: 'next/:id/media/:mediaId/point-cloud',
            lazy: () =>
              import('./map/next/point-cloud-next').then((m) => ({
                Component: m.PointCloudNext,
              })),
          },
          // Legacy missions
          {
            path: 'missions/plan-managed',
            lazy: lazyMission,
          },
          {
            path: 'missions/plan',
            lazy: lazyMission,
          },
          {
            path: 'incoming/:id',
            lazy: lazyMission,
          },
          {
            path: 'missions/:id/overview',
            lazy: lazyMission,
          },
          {
            path: 'missions/:id/map',
            lazy: lazyMission,
          },
          {
            path: 'missions/:id/media/:mediaId',
            lazy: lazyMission,
          },
          {
            path: 'missions/:id/media/:mediaId/point-cloud',
            lazy: () =>
              import('./map/map/media-2/point-cloud').then((m) => ({
                Component: m.PointCloud,
              })),
          },
          {
            path: 'missions/:id/media',
            lazy: lazyMission,
          },
          {
            path: 'missions/:id/new-assessment',
            lazy: lazyMission,
          },
          {
            path: 'missions/:id',
            lazy: lazyMission,
          },
          {
            path: 'assessments/:assessmentId',
            lazy: () =>
              import('./map/assessment/View').then((m) => ({
                Component: m.default,
              })),
          },
        ],
      },
      // 404 not found
      {
        path: '*',
        element: <NotFoundScreen />,
      },
    ],
  },
]);

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  // React Router data-router commits the URL only after lazy() resolves, so
  // window.location still points to the source page during a chunk failure.
  // Use the router's pending navigation location as the reload target so the
  // user lands on the page they were navigating to, not the one they came from.
  const pending = router.state.navigation.location;
  const targetUrl = pending
    ? pending.pathname + pending.search + pending.hash
    : undefined;
  reloadForChunkError(targetUrl);
});

const MetaBarConnected = () => {
  const meta = useSelector((state: RootState) => state.meta);
  return <MetaBar {...meta} />;
};

// Maintenance mode is handled server-side now (@birdi/ssr maintenanceMiddleware,
// toggled by the MAINTENANCE_MODE env var) so it works without the database.
const Index = () => {
  return (
    <StrictMode>
      <AppErrorBoundary>
        <PostHogProvider client={posthog}>
          <Provider store={store}>
            <ThemeProvider>
              <RouterProvider router={router} />
              <Suspense fallback={null}>
                <MetaBarConnected />
              </Suspense>
            </ThemeProvider>
          </Provider>
        </PostHogProvider>
      </AppErrorBoundary>
    </StrictMode>
  );
};

getInitialAuthState()(store.dispatch);

const root = createRoot(document.getElementById('root')!);
root.render(<Index />);
